import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useInitializeAnalyticsTools } from "~/hooks/useInitializeAnalyticsTools";
import { useAccount, usePublicAccount } from "~/swr-hooks/account/useAccount";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
const defaultValue = {
    data: undefined
};
const AccountProviderContext = /*#__PURE__*/ createContext(defaultValue);
export const AccountProvider = (param)=>{
    let { children } = param;
    const { data: account, error } = useAccount();
    const { initializeAnalyticsTools } = useInitializeAnalyticsTools();
    const value = useMemo(()=>({
            data: account,
            error
        }), [
        account,
        error
    ]);
    useEffect(()=>{
        if (account) {
            initializeAnalyticsTools(account);
        }
    }, [
        account,
        initializeAnalyticsTools
    ]);
    return /*#__PURE__*/ _jsx(AccountProviderContext.Provider, {
        value: value,
        children: children
    });
};
export const PublicAccountProvider = (param)=>{
    let { children } = param;
    const { data: account, error } = usePublicAccount();
    const { initializeAnalyticsTools } = useInitializeAnalyticsTools();
    /**
   * usePublicAccount may return:
   * - account: when user is logged in
   * - null: when user is not logged in
   * - undefined: when there was an error during getting current account
   * But all our components expects account to of type Account | undefined
   * to simplify code, if it is null, we will convert it to undefined
   */ const value = useMemo(()=>({
            data: account !== null && account !== void 0 ? account : undefined,
            error
        }), [
        account,
        error
    ]);
    useEffect(()=>{
        if (account) {
            initializeAnalyticsTools(account);
        }
    }, [
        account,
        initializeAnalyticsTools
    ]);
    return /*#__PURE__*/ _jsx(AccountProviderContext.Provider, {
        value: value,
        children: children
    });
};
export const useAccountContext = ()=>{
    const context = useContext(AccountProviderContext);
    if (context === defaultValue) {
        const error = "AccountProviderContext used outside of AccountProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
